import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getPageGeometry, streamStudentFile } from '../../../services/fileCabinetService';
import { CITE_KINDS, highlightRects } from './citationModel';

let pdfjsPromise = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/build/pdf').then((lib) => {
      const pdfjs = lib.default?.getDocument ? lib.default : lib;
      // Copied verbatim into public/ by scripts/copy-pdf-worker.js (prestart/prebuild); bundling it
      // through webpack/Babel breaks the worker with require() calls.
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

const docCache = new Map(); // `${studentId}:${fileId}` → { promise, progress, listeners }

function loadDocument(studentId, fileId, onProgress) {
  const key = `${studentId}:${fileId}`;
  let entry = docCache.get(key);
  if (!entry) {
    entry = { progress: 0, listeners: new Set() };
    entry.promise = Promise.all([
      loadPdfjs(),
      streamStudentFile(studentId, fileId, {
        onDownloadProgress: (e) => {
          if (!e.total) return;
          entry.progress = Math.round((100 * e.loaded) / e.total);
          entry.listeners.forEach((fn) => fn(entry.progress));
        },
      }),
    ]).then(async ([pdfjs, res]) => {
      const data = new Uint8Array(await res.data.arrayBuffer());
      return pdfjs.getDocument({ data }).promise;
    });
    entry.promise.catch(() => docCache.delete(key));
    docCache.set(key, entry);
  }
  if (onProgress) {
    entry.listeners.add(onProgress);
    onProgress(entry.progress);
  }
  return entry;
}

/** Start downloading a document in the background so the first citation opens fast. */
export function prefetchSourceDocument(studentId, fileId) {
  if (studentId && fileId) loadDocument(studentId, fileId).promise.catch(() => {});
}

const isTyping = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
const GAP = 12;

/**
 * Render one page to a JPEG object URL at the displayed width. Pages are rendered
 * once and kept as images (~100–250 KB each), so every page of the document stays
 * visible while scrolling — canvases for 280 pages would not fit in memory.
 */
async function renderPageImage(pdf, num, width) {
  const page = await pdf.getPage(num);
  const base = page.getViewport({ scale: 1 });
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: (width / base.width) * ratio });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  page.cleanup();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  canvas.width = 0;
  canvas.height = 0;
  return blob ? URL.createObjectURL(blob) : null;
}

/** One page in the vertical scroll: its rendered image, or a placeholder until ready. */
function PageSlot({ num, width, height, src, marks, slotRef }) {
  return (
    <div
      ref={slotRef}
      data-page={num}
      className="relative mx-auto bg-white shadow-sm dark:bg-slate-200"
      style={{ width, height }}
    >
      {src ? (
        <img src={src} alt={`Page ${num}`} className="block select-none" style={{ width, height }} draggable={false} />
      ) : (
        <span className="absolute inset-0 grid place-items-center text-[11px] text-gray-400">Rendering page {num}…</span>
      )}
      {marks.map((m, i) => (
        <span
          key={i}
          className="pointer-events-none absolute rounded-sm bg-yellow-300/40 ring-2 ring-yellow-400"
          style={{ left: m.left, top: m.top, width: m.width, height: m.height }}
        />
      ))}
      <span className="absolute right-1.5 top-1.5 rounded bg-black/45 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white">{num}</span>
    </div>
  );
}

/**
 * Split-view source panel. All pages scroll vertically like a PDF reader; the cited
 * page is scrolled into view with the quote (born-digital or OCR words) or the form
 * checkbox highlighted. J / K step through the section's citations; Esc closes.
 */
export default function SourcePanel({ studentId, request, onClose, onNavigate, onOpenFull }) {
  const scrollRef = useRef(null);
  const slotRefs = useRef({});
  const [pdf, setPdf] = useState(null);
  const [pdfKey, setPdfKey] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [sizes, setSizes] = useState([]); // [{w, h}] in PDF points, per page
  const [width, setWidth] = useState(560);
  const [zoom, setZoom] = useState(1);
  const [images, setImages] = useState({}); // page → rendered image URL
  const currentRef = useRef(1);
  const [current, setCurrent] = useState(1);
  const [marks, setMarks] = useState({ page: null, rects: [], how: null });
  const [retry, setRetry] = useState(0);

  const isOpen = Boolean(request);
  const fileId = request?.fileId;
  const page = request?.page;
  const seq = Array.isArray(request?.sequence) ? request.sequence : null;
  const idx = request?.index ?? 0;
  const pageWidth = Math.round(width * zoom);
  // Each page keeps its own aspect ratio (Letter, Legal, landscape scans).
  const heights = sizes.map((sz) => Math.round((pageWidth * sz.h) / sz.w));
  const offsets = heights.reduce((acc, h, i) => { acc.push(i ? acc[i - 1] + heights[i - 1] + GAP : 0); return acc; }, []);
  const offsetOf = (n) => offsets[n - 1] ?? 0;
  const sizeOf = (n) => sizes[n - 1] || { w: 612, h: 792 };

  const step = useCallback((delta) => {
    if (!seq?.length || !onNavigate) return;
    const next = (idx + delta + seq.length) % seq.length;
    onNavigate({ ...seq[next], sequence: seq, index: next });
  }, [seq, idx, onNavigate]);

  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => {
      if (isTyping(document.activeElement)) return;
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'j' || e.key === 'J') step(1);
      else if (e.key === 'k' || e.key === 'K') step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request, onClose, step]);

  // Panel width → page width.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(280, el.clientWidth - 48));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  // Load the document (cached per file; progress while downloading).
  useEffect(() => {
    if (!request || !fileId) return undefined;
    const key = `${studentId}:${fileId}`;
    if (key === pdfKey && pdf) return undefined;
    let cancelled = false;
    setError(null);
    setPdf(null);
    const entry = loadDocument(studentId, fileId, (p) => { if (!cancelled) setProgress(p); });
    entry.promise
      .then(async (doc) => {
        if (cancelled) return;
        const all = [];
        for (let i = 1; i <= doc.numPages; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const vp = (await doc.getPage(i)).getViewport({ scale: 1 });
          all.push({ w: vp.width, h: vp.height });
        }
        if (cancelled) return;
        setSizes(all);
        setPdf(doc);
        setPdfKey(key);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Could not load the document');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, fileId, retry, isOpen]);

  // Current page follows the scroll position.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !pdf) return undefined;
    const onScroll = () => {
      const mid = root.scrollTop + root.clientHeight / 3;
      let n = 1;
      while (n < offsets.length && offsets[n] <= mid) n += 1;
      setCurrent(n);
      currentRef.current = n;
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, pageWidth, sizes]);

  useEffect(() => { if (page) currentRef.current = page; }, [page]);

  // Render the whole document in the background, nearest-to-the-reader first, and
  // keep every page as an image so no page is ever blank once rendered.
  useEffect(() => {
    if (!pdf || !pageWidth) return undefined;
    let cancelled = false;
    const urls = [];
    setImages({});
    (async () => {
      const total = pdf.numPages;
      const done = new Set();
      let pending = {};
      let lastFlush = 0;
      while (!cancelled && done.size < total) {
        const target = currentRef.current || 1;
        let next = null;
        for (let d = 0; d < total && next == null; d += 1) {
          if (target + d <= total && !done.has(target + d)) next = target + d;
          else if (target - d >= 1 && !done.has(target - d)) next = target - d;
        }
        if (next == null) break;
        done.add(next);
        try {
          // eslint-disable-next-line no-await-in-loop
          const url = await renderPageImage(pdf, next, pageWidth);
          if (url) {
            urls.push(url);
            if (cancelled) break;
            pending[next] = url;
          }
        } catch {
          /* a page that fails to render keeps its placeholder */
        }
        const now = Date.now();
        if (now - lastFlush > 200 || done.size === total) {
          const batch = pending;
          pending = {};
          lastFlush = now;
          setImages((prev) => ({ ...prev, ...batch }));
        }
      }
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [pdf, pageWidth]);

  // Highlight geometry for the cited page.
  useEffect(() => {
    if (!request || !fileId || !page) return undefined;
    let cancelled = false;
    setMarks({ page, rects: [], how: 'loading' });
    getPageGeometry(studentId, fileId, page)
      .then((res) => {
        if (cancelled) return;
        const geo = res?.data?.data || res?.data || {};
        const hit = highlightRects(geo, request);
        setMarks({ page, ...hit });
      })
      .catch(() => { if (!cancelled) setMarks({ page, rects: [], how: null }); });
    return () => { cancelled = true; };
  }, [studentId, fileId, page, request]);

  // Scroll the cited page (and its highlight) into view.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !pdf || !page) return;
    const top = offsetOf(page);
    const first = marks.page === page && marks.rects.length ? marks.rects[0] : null;
    const scale = marks.unit === 'frac' ? heights[page - 1] || 0 : pageWidth / sizeOf(page).w;
    const inner = first ? Math.max(0, first.top * scale - 140) : 0;
    root.scrollTo({ top: top + inner, behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, page, pageWidth, sizes, marks]);

  if (!request) return null;
  const meta = CITE_KINDS[request.kind] || CITE_KINDS.page;
  const numPages = pdf?.numPages || 0;
  const markH = heights[(marks.page || 1) - 1] || 0;
  const markScale = pageWidth / sizeOf(marks.page || 1).w;
  const pxRects = (marks.rects || []).map((r) => {
    const sx = marks.unit === 'frac' ? pageWidth : markScale;
    const sy = marks.unit === 'frac' ? markH : markScale;
    const pad = 3;
    return {
      left: r.x0 * sx - pad,
      top: r.top * sy - pad,
      width: (r.x1 - r.x0) * sx + pad * 2,
      height: (r.bottom - r.top) * sy + pad * 2,
    };
  });
  const jumpTo = (n) => {
    const p = Math.min(Math.max(1, n), numPages || 1);
    scrollRef.current?.scrollTo({ top: offsetOf(p), behavior: 'smooth' });
  };

  return (
    <aside
      className="fixed inset-y-0 right-0 z-[60] flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:w-[min(46vw,760px)]"
      aria-label="Source document"
    >
      <header className="flex items-start gap-3 border-b border-gray-200 px-4 py-3 dark:border-slate-700">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
            Source · {meta.label}
          </p>
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {request.label || 'Citation'}
            {request.value != null && request.value !== '' ? (
              <span className="font-normal text-gray-500 dark:text-slate-400"> — {String(request.value).slice(0, 60)}</span>
            ) : null}
          </p>
          <p className="truncate text-[11px] text-gray-500 dark:text-slate-400">
            {request.fileName || 'Document'} · cited page {page}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {seq?.length > 1 ? (
            <div className="mr-1 flex items-center rounded-lg border border-gray-200 dark:border-slate-600">
              <button type="button" onClick={() => step(-1)} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-slate-300" title="Previous citation (K)" aria-label="Previous citation">▲</button>
              <span className="px-1 text-[11px] tabular-nums text-gray-500 dark:text-slate-400">{idx + 1}/{seq.length}</span>
              <button type="button" onClick={() => step(1)} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-slate-300" title="Next citation (J)" aria-label="Next citation">▼</button>
            </div>
          ) : null}
          <button type="button" onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(1)))} className="hidden rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 sm:inline-flex" title="Zoom out">−</button>
          <button type="button" onClick={() => setZoom((z) => Math.min(2.4, +(z + 0.2).toFixed(1)))} className="hidden rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 sm:inline-flex" title="Zoom in">+</button>
          {onOpenFull ? (
            <button type="button" onClick={() => onOpenFull(request)} className="rounded-lg px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-800" title="Open the whole document">Full view</button>
          ) : null}
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" aria-label="Close source panel" title="Close (Esc)">
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" /></svg>
          </button>
        </div>
      </header>

      {request.quote || marks.how === 'checkbox' || (pdf && marks.page === page && marks.how === null) ? (
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11.5px] leading-snug text-gray-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
          {request.quote ? (<><span className="font-semibold">Quote: </span>“{String(request.quote).slice(0, 240)}”</>) : null}
          {marks.how === 'checkbox' ? <span className="text-indigo-700 dark:text-indigo-300"> The highlighted box is the answer read for this field.</span> : null}
          {pdf && marks.page === page && marks.how === null ? (
            <span className={`${request.quote ? 'ml-1 ' : ''}text-amber-700 dark:text-amber-400`}>
              {request.quote
                ? `— could not mark the exact spot (handwritten or unreadable text); the quote is on page ${page}.`
                : request.quotePage && request.quotePage !== page
                  ? `This page is part of the cited source; the quoted text is on page ${request.quotePage}.`
                  : `No quote was stored for this citation, so nothing is marked on page ${page}.`}
            </span>
          ) : null}
        </div>
      ) : null}

      <div ref={scrollRef} className="relative flex-1 overflow-y-auto overflow-x-auto bg-gray-100 px-6 py-3 dark:bg-slate-950">
        {error ? (
          <div className="mt-10 text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button
              type="button"
              onClick={() => { docCache.delete(`${studentId}:${fileId}`); setRetry((r) => r + 1); }}
              className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              Try again
            </button>
          </div>
        ) : !pdf ? (
          <div className="mx-auto mt-10 w-64 text-center text-xs text-gray-500 dark:text-slate-400">
            <p>Loading document{progress ? ` — ${progress}%` : '…'}</p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
              <div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${Math.max(progress, 4)}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: GAP }}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
              <PageSlot
                key={n}
                num={n}
                width={pageWidth}
                height={heights[n - 1] || 0}
                src={images[n]}
                marks={n === marks.page ? pxRects : []}
                slotRef={(el) => { slotRefs.current[n] = el; }}
              />
            ))}
          </div>
        )}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-2 text-[11px] text-gray-500 dark:border-slate-700 dark:text-slate-400">
        <label className="flex items-center gap-1.5">
          Page
          <input
            type="number"
            min={1}
            max={numPages || 1}
            value={current}
            onChange={(e) => setCurrent(Number(e.target.value) || 1)}
            onKeyDown={(e) => { if (e.key === 'Enter') jumpTo(Number(e.currentTarget.value)); }}
            className="w-14 rounded border border-gray-200 bg-white px-1 py-0.5 text-center tabular-nums dark:border-slate-600 dark:bg-slate-800"
            aria-label="Go to page"
          />
          <span>of {numPages || '…'}</span>
          {page && current !== page ? (
            <button type="button" onClick={() => jumpTo(page)} className="ml-1 rounded border border-indigo-200 px-1.5 py-0.5 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300">
              Back to p.{page}
            </button>
          ) : null}
        </label>
        <span className="hidden sm:inline">Scroll to read · J / K next / previous citation · Esc close</span>
      </footer>
    </aside>
  );
}
